import React, { useContext, useMemo } from 'react';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Empty, Grid, Spin, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

export const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

interface SortableCardProps<T> {
  record: T;
  index: number;
  columns: TableColumnsType<T>;
}

const SortableCard = <T extends { id: number }>({ record, index, columns }: SortableCardProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: record.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 2 } : {}),
  };

  return (
    <RowContext.Provider value={{ setActivatorNodeRef, listeners }}>
      <div ref={setNodeRef} style={style} className="sortable-mobile-card" {...attributes}>
        {columns.map((column, columnIndex) => {
          if (!('dataIndex' in column) && column.key !== 'sort') return null;

          const dataIndex = 'dataIndex' in column ? column.dataIndex : undefined;
          const value = typeof dataIndex === 'string' ? (record as unknown as Record<string, unknown>)[dataIndex] : undefined;
          const content = (column.render ? column.render(value, record, index) : value) as React.ReactNode;
          const isHandle = column.key === 'sort';
          const hasTitle = typeof column.title !== 'function' && Boolean(column.title);

          return (
            <div
              className={`sortable-mobile-field${isHandle ? ' sortable-mobile-handle' : ''}${!isHandle && !hasTitle ? ' sortable-mobile-field-no-label' : ''}`}
              key={column.key?.toString() ?? columnIndex}
            >
              {!isHandle && hasTitle && (
                <span className="sortable-mobile-label">{column.title as React.ReactNode}</span>
              )}
              <div className="sortable-mobile-value">{isHandle ? <DragHandle /> : content}</div>
            </div>
          );
        })}
      </div>
    </RowContext.Provider>
  );
};

interface SortableTableProps<T> {
  loading: boolean;
  columns: TableColumnsType<T>;
  dataSource: T[];
  onDragEnd: (event: DragEndEvent) => void;
  defaultSortField?: keyof T;
}

const SortableTable = <T extends { id: number }>({
  loading,
  columns,
  dataSource,
  onDragEnd,
  defaultSortField,
}: SortableTableProps<T>) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;
  const sortedDataSource = useMemo(() => {
    if (defaultSortField) {
      return [...dataSource].sort((a, b) => {
        if (a[defaultSortField] && b[defaultSortField]) {
          return (a[defaultSortField] as any) - (b[defaultSortField] as any);
        }
        return 0;
      });
    }
    return dataSource;
  }, [dataSource, defaultSortField]);

  return (
    <>
      {(sortedDataSource.length > 0) ?
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext items={sortedDataSource.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {isMobile ? (
              <Spin spinning={loading}>
                <div className="sortable-mobile-list" aria-busy={loading}>
                  {sortedDataSource.map((record, index) => (
                    <SortableCard key={record.id} record={record} index={index} columns={columns} />
                  ))}
                </div>
              </Spin>
            ) : (
              <Table
                pagination={{ pageSize: 100 }}
                loading={loading}
                rowKey="id"
                components={{ body: { row: Row } }}
                columns={columns}
                dataSource={sortedDataSource}
                scroll={{ x: 'max-content' }}
              />
            )}
          </SortableContext>
        </DndContext> : <Empty />
      }
    </>
  );
};

export default SortableTable;
